const STAGE_TO_BUFFER_KEY = {
    thinking: 'thinking',
    requirement_analysis: 'requirementAnalysis',
    requirement_confirmation: 'requirementConfirmation',
    plan_basis: 'planBasis',
    plan_generation: 'planGeneration',
    plan_review: 'planReview',
    code_generation: 'codeGeneration',
    validation: 'validation',
    repair: 'repair',
    codesys_validation: 'codesysValidation',
    tia_tools: 'tiaTools',
};
const STAGE_STREAM_TYPES = [
    'stage_start',
    'stage_delta',
    'stage_artifact',
    'stage_done',
];
/**
 * The statuses a stage ends on when it has stopped to wait for a person.
 *
 * The browser's approval reads this too (planReviewState), rather than a copy.
 */
export const WAITING_ON_A_PERSON = ['needs_approval', 'needs_input'];
export function mapStageToBufferKey(stage) {
    return STAGE_TO_BUFFER_KEY[stage];
}
export function isStageStreamChunk(chunk) {
    if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk))
        return false;
    const record = chunk;
    return typeof record.type === 'string'
        && STAGE_STREAM_TYPES.includes(record.type)
        && typeof record.stage === 'string'
        && record.stage in STAGE_TO_BUFFER_KEY;
}
export function applyStageChunkToBuffers(buffers, chunk) {
    const stageKey = mapStageToBufferKey(chunk.stage);
    const prev = buffers?.[stageKey] || {
        started: false,
        done: false,
        text: '',
    };
    const timestamp = typeof chunk.timestamp === 'number' ? chunk.timestamp : Date.now();
    const startedAt = prev.startedAt || timestamp;
    if (chunk.type === 'stage_start') {
        // A restart discards what the stage wrote before it. Every channel goes,
        // not just the prose: a conclusion or an opening line left behind belongs
        // to the attempt that was thrown away, and the clock restarts with them so
        // the step reports how long the attempt that survived actually took.
        const restarting = chunk.restart === true;
        return {
            ...buffers,
            [stageKey]: {
                ...prev,
                started: true,
                done: false,
                status: 'streaming',
                title: chunk.title || prev.title,
                message: chunk.message || prev.message,
                text: restarting ? '' : prev.text || '',
                conclusion: restarting ? undefined : prev.conclusion,
                opening: restarting ? undefined : prev.opening,
                reasoning: restarting ? undefined : prev.reasoning,
                startedAt: restarting ? timestamp : startedAt,
                // Counted so the page can say the step restarted. The clock above goes
                // back to zero by design; without a word beside it that reads as a
                // timer that lost four minutes.
                restartCount: restarting ? (prev.restartCount || 0) + 1 : prev.restartCount,
                endedAt: undefined,
                durationMs: undefined,
                terminalReason: undefined,
                updatedAt: timestamp,
            },
        };
    }
    if (chunk.type === 'stage_delta') {
        // Four texts per stage: the prose the model wrote, the conclusion, the
        // turn's opening line, and the provider's scratch tokens. They are
        // rendered in different type and different places, so they must never
        // share a buffer.
        const channel = chunk.channel || 'thought';
        const append = (previous, active) => active ? `${previous || ''}${chunk.delta || ''}` : previous;
        return {
            ...buffers,
            [stageKey]: {
                ...prev,
                started: true,
                done: false,
                status: 'streaming',
                text: append(prev.text, channel === 'thought') || '',
                conclusion: append(prev.conclusion, channel === 'conclusion'),
                opening: append(prev.opening, channel === 'opening'),
                reasoning: append(prev.reasoning, channel === 'reasoning'),
                startedAt,
                updatedAt: timestamp,
            },
        };
    }
    if (chunk.type === 'stage_artifact') {
        return {
            ...buffers,
            [stageKey]: {
                ...prev,
                started: true,
                artifact: chunk.artifact,
                artifactType: chunk.artifactType || prev.artifactType,
                completeness: chunk.completeness || prev.completeness,
                startedAt,
                updatedAt: timestamp,
            },
        };
    }
    // A stage ends once, and keeps the clock of the run that ended it.
    //
    // `durationMs` is `timestamp - startedAt`, and `startedAt` only moves on a
    // restart — so a second `stage_done` on a stage that is already finished
    // measures from the first run's start to the second's end.
    //
    // 2026-09-16 线上：预检那一行写着「· 41m27s」，而运行时记下的检查器耗时是
    // 3分59秒。validation 收到了不止一次 done——预检结束一次，修复循环里的每次复检
    // 一次，收尾再一次——于是最后那次把整轮 41 分钟都算成了预检，在一个写着「内置
    // 规则」的标签下面。
    //
    // A stage that genuinely runs again says so with `restart`, which clears
    // `done` and moves `startedAt`; that path is untouched. What is refused here
    // is the same ending being announced twice. The status still updates: a later
    // chunk may know better how the stage ended, only not how long it took.
    const alreadyEnded = prev.done === true && typeof prev.endedAt === 'number';
    const endedAt = alreadyEnded ? prev.endedAt : timestamp;
    const durationMs = alreadyEnded && typeof prev.durationMs === 'number'
        ? prev.durationMs
        : Math.max(0, endedAt - startedAt);
    // A stage that stopped to wait on a person, and is now told how that ended,
    // has changed state rather than ended twice — and what it said while waiting
    // was the question, which the answer retires.
    //
    // 2026-09-23 线上（Siemens 回合）：11:33:11 用户批准了计划，卡片改成「已手动批准」、
    // 代码生成也开始了，「计划审批」这一步却整轮都写着「等待计划确认。」——plan_review 以
    // needs_approval 结束时这句话进了结论，批准后的 stage_done('completed') 又让先写的赢。
    //
    // Only a stage that was waiting. validation publishes a conclusion on every
    // status change, and its row states what the preflight saw; that one still
    // keeps its first conclusion.
    const answered = prev.done === true
        && prev.status !== undefined
        && WAITING_ON_A_PERSON.includes(prev.status)
        && chunk.status !== prev.status;
    return {
        ...buffers,
        [stageKey]: {
            ...prev,
            started: true,
            done: true,
            status: chunk.status,
            // A streamed conclusion outranks the one attached at completion. Once a
            // wait is answered, the answer's own conclusion stands — and with none
            // attached the field is left empty, so the page derives it from the state
            // the stage is in now.
            conclusion: answered ? chunk.conclusion : prev.conclusion || chunk.conclusion,
            completeness: chunk.completeness || prev.completeness,
            terminalReason: chunk.terminalReason || prev.terminalReason,
            startedAt,
            endedAt,
            durationMs,
            updatedAt: timestamp,
        },
    };
}
/**
 * The stage-buffer field for a partial message update.
 *
 * Yields no field at all when this stream has produced no stage chunks yet.
 * Message updates are spread onto the message, so a literal `undefined` here
 * does not mean "leave it alone" — it erases whatever is already there. A turn
 * can be written by more than one stream, and the later one starts with an
 * empty accumulator and force-flushes on its first chunk; writing `undefined`
 * at that moment wipes the whole account of the turn off the screen, which is
 * what emptied the step stream as soon as a clarification card arrived.
 *
 * Never clears, only merges. Clearing is not something a stream needs to do.
 */
export function stageBufferPatch(accumulated) {
    return accumulated && Object.keys(accumulated).length ? { stageBuffers: accumulated } : {};
}
//# sourceMappingURL=stageBuffers.js.map